'use client';
import { useEffect, useState } from 'react';
import { deviceHeader } from '../../lib/device';
import { useT } from '../../lib/i18n';
import { PwInput, SocialButtons } from '../../components/FormBits';
import { SmartLink } from '../../components/SmartLink';
import { Working } from '../../components/Working';

/**
 * 로그인 폼 — 아이디·비밀번호·ID 저장·찾기·소셜·회원가입(가이드 login.html).
 *
 * 실패는 **에러 코드로 갈라 다루는 것이 계약**이다(서버 스모크가 코드를 고정한다) —
 * `AUTH_NOT_VERIFIED` 면 문구가 아니라 **인증 메일 재발송 버튼**을 띄운다(현행은 재인증 유도).
 * 성공하면 `?next=` 로 돌아간다 — 로그인이 필요해 튕긴 화면으로 되돌리기 위함이다.
 */
export function LoginForm({ social, title, autoLogin }: {
  social: { google: boolean; apple: boolean; kakao: boolean; naver: boolean };
  /** 자동 로그인 — 마켓이 끄면 칸을 그리지 않는다(`enabled=false`) */
  autoLogin: { enabled: boolean; days: number };
  title: string;
}) {
  const t = useT();
  const [uid, setUid] = useState('');
  const [pw, setPw] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 인증 미완료 — 재발송 흐름으로 넘어간다(코드로 판정) */
  const [notVerified, setNotVerified] = useState(false);
  const [resent, setResent] = useState(false);

  /*
   * 뒤로 가기로 되돌아온 로그인 화면(2026-09-17 사용자 제보) — 서버 가드(page.tsx)가 이때는 돌지 않는다.
   * 브라우저가 **요청 없이** 메모리에서 화면을 되살리기 때문이다. 되살아난 순간에만 한 번 물어보고,
   * 아직 로그인 상태면 그 자리를 홈으로 바꿔치운다. 처음 들어온 방문자에게는 요청이 늘지 않는다.
   */
  useEffect(() => {
    const onShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      void fetch('/api/auth/me')
        .then((r) => {
          if (!r.ok) return;
          const next = new URLSearchParams(window.location.search).get('next');
          window.location.replace(next?.startsWith('/') && !next.startsWith('//') ? next : '/');
        })
        .catch(() => undefined);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);

  const submit = async () => {
    setError(null); setNotVerified(false);
    if (!uid.trim() || !pw) { setError(t('acc.err.loginRequired')); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        /*
         * 기기 판정을 **브라우저가 붙인다**(2026-09-17 사용자 제보 "PC 와 휴대폰인데 동시접속 차단이 걸린다").
         * 동시접속 차단은 같은 기기 종류끼리만 끊는데, 아이패드가 데스크톱 UA 를 보내 PC 로 묶였다.
         * 서버는 UA 로 그것을 가릴 수 없다 — 여기서 터치 지점까지 보고 정한다(`lib/device.ts`).
         */
        headers: { 'content-type': 'application/json', ...deviceHeader() },
        body: JSON.stringify({ uid: uid.trim(), password: pw, keepSignedIn }),
      });
      if (res.ok) {
        const next = new URLSearchParams(window.location.search).get('next');
        window.location.href = next && next.startsWith('/') ? next : '/';
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
      if (body.code === 'AUTH_NOT_VERIFIED') { setNotVerified(true); return; }
      setError(body.message ?? t('acc.err.loginFailed'));
    } catch {
      setError(t('acc.err.network'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 인증 메일 재발송 — **아이디 기준**(서버가 그 회원의 가입 메일로 보낸다).
   * 화면은 이메일을 모르므로 이 경로가 필요했다. 없는 아이디여도 같은 응답이 온다(계정 탐색 방지).
   */
  const resend = async () => {
    setBusy(true);
    try {
      await fetch('/api/members/resend-auth', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ uid: uid.trim() }),
      });
      setResent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="form-box login-box">
      <div className="form-wrap">
        <div className="form-field">
          <input type="text" className="form-input" placeholder={t('acc.uid')} value={uid} autoComplete="username"
            onChange={(e) => setUid(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
        </div>
        <div className="form-field">
          <PwInput value={pw} onChange={setPw} placeholder={t('acc.password')} autoComplete="current-password"
            onEnter={() => void submit()} />
        </div>
        {error && <p className="form-hint" style={{ color: '#dc2626' }}>{error}</p>}
        {notVerified && (
          <div className="form-hint" style={{ color: '#dc2626' }}>
            <p>{t('acc.notVerified')}</p>
            {resent
              ? <p style={{ color: 'var(--accent)' }}>{t('acc.resentMail')}</p>
              : (
                <button type="button" className="action-btn action-cancel btn-sm" disabled={busy}
                  onClick={() => void resend()}>{t('acc.resendMail')}</button>
              )}
          </div>
        )}
        <button className="login-submit-btn" disabled={busy} onClick={() => void submit()}>
          {busy && <Working inline />}{title}
        </button>
      </div>

      <div className="login-options">
        {/*
          자동 로그인(2026-08-25 사용자 확정) — 예전 `아이디 저장` 자리다. 아이디만 채워 주는
          것은 비밀번호를 매번 치게 해서 체감 이득이 작았다. 유지 기간은 마켓이 정한다.
          끈 마켓에서는 칸 자체를 그리지 않는다 — 세션 만료를 짧게 둔 보안 정책을 이 기능이
          무력화하면 안 되기 때문이다(서버도 마켓 스위치를 다시 본다).
        */}
        {autoLogin.enabled ? (
          <label className="form-check-item login-save-id">
            <input type="checkbox" checked={keepSignedIn} onChange={(e) => setKeepSignedIn(e.target.checked)} />
            {' '}{t('acc.keepSignedIn')}
          </label>
        ) : <span />}
        {/*
          문구는 예전 한 줄 그대로(`아이디 · 비밀번호 찾기`)이고 **두 조각에 각각 링크**를 건다
          (2026-08-25 사용자 지시) — 누른 쪽 탭이 바로 열린다. 구분자는 여기서 넣는다.
        */}
        <span className="login-find-links">
          <SmartLink href="/find?tab=id" className="login-find-link">{t('acc.findLinkId')}</SmartLink>
          <span className="login-find-sep">·</span>
          <SmartLink href="/find?tab=pw" className="login-find-link">{t('acc.findLinkPw')}</SmartLink>
        </span>
      </div>
      <p className="login-security-hint">{t('acc.securityHint')}</p>

      {(social.google || social.apple || social.kakao || social.naver) && (
        <>
          <div className="form-divider login-divider">{t('acc.or')}</div>
          <SocialButtons social={social} mode="login" />
        </>
      )}

      <div className="form-divider login-divider">{t('acc.or')}</div>
      <div className="login-register-row">
        <SmartLink href="/join" className="login-register-btn">{t('acc.join')}</SmartLink>
      </div>
    </div>
  );
}
